'use client'

import React, { PropsWithChildren } from 'react'
import ArrowRightIcon from '@heroicons/react/24/outline/ArrowLongRightIcon'
import Image from 'next/image'

import { Listing } from '@/data/getListing'
import { ActionLink } from '../ActionLink'
import { PlayIcon } from '../icons/PlayIcon'
import { Logo } from '../Logo'
import { ModalGalleryContext } from '../ModalGallery/GalleryContext'
import { SectionContainer } from '../SectionContainer'
import { SectionLayout } from '../SectionLayout'
import { remapListingAssetsToGallery } from './listingImages'

type ListingHeaderProps = PropsWithChildren<{
  listing: Listing
}>

export const ListingHeader = ({ children, listing }: ListingHeaderProps) => {
  const [, sendToModalGallery] = ModalGalleryContext.useActor()
  const { videoIndex } = remapListingAssetsToGallery(listing)
  const imageUrl = listing.image?.url

  const showGallery = () => sendToModalGallery({ type: 'Activate gallery', index: videoIndex ?? undefined })

  return (
    <SectionContainer name="Listing" theme="dark">
      <div className="relative inset-0 z-0 h-16 bg-neutral-850 md:h-24" />
      <SectionLayout
        fullWidth
        // 200vh allows the page to grow wide and not super tall. But if they have a mega size screen it all grows to fit.
        innerClassName="max-w-[200vh] h-auto w-full md:flex-row justify-stretch"
        className="h-full"
      >
        <div className="flex md:w-1/2 md:items-stretch">{children}</div>
        <div className="group relative aspect-square w-full cursor-pointer overflow-hidden md:w-1/2">
          {imageUrl ? (
            <Image
              src={'https:' + imageUrl}
              loading="eager"
              fill
              alt=""
              sizes="(max-width: 960px) 100vw, 75vw"
              className="object-cover"
              onClick={showGallery}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-black text-white" onClick={showGallery}>
              <Logo className="h-auto w-32" />
            </div>
          )}
          {videoIndex && (
            <PlayIcon className="pointer-events-none absolute left-1/2 top-1/2 -ml-10 -mt-10 h-20 w-20 opacity-100 transition-opacity duration-1000 group-hover:opacity-50 group-hover:duration-500" />
          )}
        </div>
        {!videoIndex && (
          // Replicate the onClick here same as the image, can't remove pointer events without killing hover events
          <div className="group absolute inset-0 hidden w-1/2 flex-col translate-x-full md:flex" onClick={showGallery}>
            <div className="pointer-events-none w-full grow"></div>
            <div className="sticky bottom-0">
              <div className="absolute inset-x-0 bottom-0 h-28 grow bg-gradient-to-t from-neutral-850/80 via-neutral-850/60 via-50%"></div>

              <div className="absolute bottom-7 left-8 pb-[8px] pl-2.5 pr-1.5 pt-[5.5px] transition-colors duration-1000">
                <ActionLink
                  arrow={
                    <ArrowRightIcon
                      strokeWidth="1.0"
                      className="absolute right-0 w-10 text-current transition-transform delay-300 translate-y-px group-hover:translate-x-1"
                    />
                  }
                  className="pr-10"
                >
                  <span className="inline-flex flex-col pb-0">
                    View Gallery
                    <span className="-mt-1 inline-block border-b-2 border-b-current"></span>
                  </span>
                </ActionLink>
              </div>
            </div>
          </div>
        )}
      </SectionLayout>
    </SectionContainer>
  )
}
